import { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar, Clock, Users, CheckCircle, ChevronRight,
  Brain, MessageSquare, Search, Layers, ArrowRight,
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

const clientQuotes = [
  '"I know what to do, but I don\'t do it."',
  '"Part of me wants to, but another part of me…"',
  '"I\'m just not the kind of person who…"',
  '"People will judge me."',
  '"I have to get it perfect first."',
];

const agendaItems = [
  { icon: <Layers className="w-5 h-5" />, text: 'How to feed a session transcript or audio into the analysis' },
  { icon: <Brain className="w-5 h-5" />, text: 'What the 14-section report actually surfaces — and how to read it' },
  { icon: <MessageSquare className="w-5 h-5" />, text: 'How to use the suggested next-session questions in your practice' },
  { icon: <Search className="w-5 h-5" />, text: 'A live demo on a real coaching conversation' },
  { icon: <CheckCircle className="w-5 h-5" />, text: 'How to cut your session prep in half or more with Session Intelligence™ integrated into your existing workflow' },
];

const professions = [
  'Coaching (life, executive, business, performance)',
  'NLP and Time Line Therapy',
  'Hypnotherapy',
  'Counselling and psychotherapy',
  'Leadership development and team coaching',
  'Wellness, transformation, and personal development',
  'Training schools, certification programs, and NLP/hypnotherapy institutes — who want to offer students a valuable AI tool and create recurring revenue',
];

const closingQuestions = [
  '"What was really going on underneath that conversation?"',
  '"What did I miss?"',
  '"What should I explore next time?"',
  '"What question would unlock the next breakthrough?"',
];

export function WebinarRegistrationPage() {
  const scrollToForm = () => {
    document.getElementById('register')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://link.msgsndr.com/js/form_embed.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-20 text-center">
        <div className="max-w-3xl mx-auto">
          <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible">
            <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 uppercase tracking-wide">
              Session Intelligence™ — Live Launch Webinar
            </div>
          </motion.div>

          <motion.h1
            custom={1} variants={fadeUp} initial="hidden" animate="visible"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6"
          >
            The Sentence Your Client Said That You Only Caught Three Days Later
          </motion.h1>

          <motion.p
            custom={2} variants={fadeUp} initial="hidden" animate="visible"
            className="text-slate-300 text-lg leading-relaxed mb-8 max-w-2xl mx-auto"
          >
            A live session showing coaches, NLP practitioners, hypnotherapists, and counsellors how to use AI-assisted session analysis to surface the limiting beliefs, parts conflicts, and Meta Model patterns hiding in plain sight inside every coaching conversation.
          </motion.p>

          <motion.div custom={3} variants={fadeUp} initial="hidden" animate="visible" className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={scrollToForm}
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-8 py-4 rounded-xl text-base transition-colors shadow-lg"
            >
              Reserve Your Seat <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>

          <motion.p custom={4} variants={fadeUp} initial="hidden" animate="visible" className="text-slate-400 text-sm mt-4">
            Limited seats to keep the session interactive
          </motion.p>

          <motion.div custom={5} variants={fadeUp} initial="hidden" animate="visible" className="mt-10 flex flex-col items-center gap-2 text-slate-400 text-sm">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-teal-400" /> Thursday, 25 June &bull; 45 minutes</div>
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-teal-400" /> Limited seats</div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 text-slate-300 text-sm">
              <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-teal-400" /> 10:00 AM EST (Toronto/New York)</div>
              <span className="text-slate-500">·</span>
              <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-teal-400" /> 3:00 PM GMT (London)</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="px-4 py-20 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-6">You Know the Sentence.</h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              The one your client said that you only caught three days later — replaying the session in your head at 11pm, or on the drive home, or halfway through the next session when they said something that suddenly made the first thing click.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mb-10">
              The one that, if you'd caught it in the room, would have changed everything.
            </p>
          </motion.div>

          <div className="space-y-3 mb-10">
            {clientQuotes.map((quote, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                className="border-l-4 border-teal-400 pl-5 py-3 bg-slate-50 rounded-r-xl"
              >
                <p className="text-slate-700 text-base italic">{quote}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          >
            <p className="text-slate-600 text-lg leading-relaxed mb-4">
              These aren't throwaway lines. They're signals — possible limiting beliefs, emotional charge, inner conflict, identity patterns, places where the client may be stuck beneath the surface.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed">
              The challenge isn't skill. It's that when you're holding the session — listening deeply, managing the process, asking questions, supporting the client — your brain is doing four jobs at once. No human catches every pattern in real time. Not even the best trainers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Shift Section */}
      <section className="px-4 py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              What If You Could See Every Pattern the Moment It Appears?
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              Session Intelligence™ is AI-assisted coaching session analysis that helps you identify possible limiting beliefs, emotional language, contradictions, parts conflicts, Meta Model patterns — and suggests next-session questions from real coaching conversations.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 text-left">
              {[
                { label: 'Not therapy', desc: 'No clinical diagnosis or treatment recommendations.' },
                { label: 'Not diagnosis', desc: 'Pattern identification from language, not medical assessment.' },
                { label: 'Not AI replacing the coach', desc: 'A clearer map so the human coach can coach more effectively.' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                    <span className="text-slate-800 font-semibold text-sm">{item.label}</span>
                  </div>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You'll See */}
      <section className="px-4 py-20 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">Here's What I'll Walk You Through</h2>
          </motion.div>
          <div className="space-y-4">
            {agendaItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className="flex items-start gap-4 bg-slate-50 rounded-2xl p-5"
              >
                <div className="text-teal-500 mt-0.5 flex-shrink-0">{item.icon}</div>
                <p className="text-slate-700 text-base leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="px-4 py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">This Webinar Is For You If You Work With Clients In:</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
            {professions.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.4 }}
                className={`flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-slate-200${i === professions.length - 1 ? ' sm:col-span-2' : ''}`}
              >
                <ChevronRight className="w-4 h-4 text-teal-500 flex-shrink-0" />
                <span className="text-slate-700 text-sm">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Questions */}
      <section className="px-4 py-20 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-8">
              If You've Ever Finished a Session and Thought:
            </h2>
          </motion.div>
          <div className="space-y-4 mb-10">
            {closingQuestions.map((q, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                className="text-slate-600 text-lg italic"
              >
                {q}
              </motion.p>
            ))}
          </div>
          <motion.p
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="text-slate-700 text-xl font-semibold"
          >
            Then this webinar is for you.
          </motion.p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 py-16 bg-slate-900 text-center">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">Reserve Your Seat for the Live Launch</h2>
            <p className="text-slate-400 text-base mb-1">Thursday, 25 June &bull; 45 minutes</p>
            <p className="text-slate-300 text-base mb-2">10:00 AM EST (Toronto/New York) &bull; 3:00 PM GMT (London)</p>
            <p className="text-slate-400 text-sm mb-8">Seats are limited to keep the session interactive.</p>
            <button
              onClick={scrollToForm}
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-8 py-4 rounded-xl text-base transition-colors shadow-lg"
            >
              Register Now <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-slate-500 text-sm mt-4">Free to attend. No replay for non-attendees.</p>
          </motion.div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="register" className="px-4 py-16 bg-slate-900">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl font-bold text-white mb-2">Save Your Spot</h2>
            <p className="text-slate-400 text-base">Enter your details and we'll send you everything you need.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.5 }}
          >
            <iframe
              src="https://api.leadconnectorhq.com/widget/form/VovmrEaZU5OmL4J1kn90"
              style={{ width: '100%', height: '511px', border: 'none', borderRadius: '8px' }}
              id="inline-VovmrEaZU5OmL4J1kn90"
              data-layout='{"id":"INLINE"}'
              data-trigger-type="alwaysShow"
              data-trigger-value=""
              data-activation-type="alwaysActivated"
              data-activation-value=""
              data-deactivation-type="neverDeactivate"
              data-deactivation-value=""
              data-form-name="SI Reg Page"
              data-height="511"
              data-layout-iframe-id="inline-VovmrEaZU5OmL4J1kn90"
              data-form-id="VovmrEaZU5OmL4J1kn90"
              title="SI Reg Page"
            />
          </motion.div>

          <p className="text-xs text-slate-500 text-center mt-5 leading-relaxed">
            Your information is private. We'll never share it. You'll get the webinar link and occasional emails about Session Intelligence™ — unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 px-4 py-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-3">
          <img
            src="/Patrick_Profile_Under_2MB_q95.jpg"
            alt="Patrick Dahdal"
            className="w-10 h-10 rounded-full object-cover border-2 border-teal-400/50"
          />
          <div className="text-left">
            <p className="text-white text-sm font-semibold">Patrick Dahdal</p>
            <p className="text-slate-400 text-xs">Founder, Human Potential OS™</p>
          </div>
        </div>
        <p className="text-slate-500 text-xs">© {new Date().getFullYear()} Human Potential OS™. All rights reserved.</p>
      </footer>
    </div>
  );
}
